import type { AgeGroup, MatchStatsRead, TeamGender } from './endpoints.ts'

/** The match format, players a side: 7, 9 or 11 contro, as the contract spells it. */
export type PlayersPerSide = NonNullable<MatchStatsRead['players_per_side']>

/** A category Volée admits: the API refuses a team or a league category outside this list. */
export interface AdmittedCategory {
  readonly age_group: AgeGroup
  readonly under_age: number
  readonly gender: TeamGender
  readonly players_per_side: PlayersPerSide
}

// Mirrors FORMATS in services/api/src/volee_api/core/match_formats.py, row for row and in order:
// services/api/tests/test_match_formats.py reads this file and fails on any difference.
export const ADMITTED_CATEGORIES: readonly AdmittedCategory[] = [
  { age_group: 'pulcini', under_age: 10, gender: 'open', players_per_side: 7 },
  { age_group: 'pulcini', under_age: 11, gender: 'open', players_per_side: 7 },
  { age_group: 'pulcini', under_age: 0, gender: 'open', players_per_side: 7 },
  { age_group: 'esordienti', under_age: 12, gender: 'open', players_per_side: 9 },
  { age_group: 'esordienti', under_age: 13, gender: 'open', players_per_side: 9 },
  { age_group: 'esordienti', under_age: 0, gender: 'open', players_per_side: 9 },
  { age_group: 'giovanissimi', under_age: 14, gender: 'male', players_per_side: 11 },
  { age_group: 'giovanissimi', under_age: 15, gender: 'male', players_per_side: 11 },
  { age_group: 'giovanissimi', under_age: 15, gender: 'female', players_per_side: 9 },
  { age_group: 'allievi', under_age: 16, gender: 'male', players_per_side: 11 },
  { age_group: 'allievi', under_age: 17, gender: 'male', players_per_side: 11 },
  { age_group: 'allievi', under_age: 17, gender: 'female', players_per_side: 11 },
  { age_group: 'juniores', under_age: 19, gender: 'male', players_per_side: 11 },
  { age_group: 'juniores', under_age: 19, gender: 'female', players_per_side: 11 },
  { age_group: 'seniores', under_age: 0, gender: 'male', players_per_side: 11 },
  { age_group: 'seniores', under_age: 0, gender: 'female', players_per_side: 11 },
]

/** One string per category, for a single select: «allievi:17:male». */
export function categoryKey(category: {
  readonly age_group: AgeGroup
  readonly under_age: number
  readonly gender: TeamGender
}): string {
  return `${category.age_group}:${String(category.under_age)}:${category.gender}`
}

export function admittedCategory(key: string): AdmittedCategory | undefined {
  return ADMITTED_CATEGORIES.find((category) => categoryKey(category) === key)
}

/** The three columns of a team or a league category, as a picked key spells them. */
export interface CategoryColumns {
  readonly age_group: AgeGroup
  readonly under_age: number
  readonly gender: TeamGender
}

/** The body's three columns for a picked key; `undefined` for nothing picked or a key not admitted. */
export function categoryColumns(key: string): CategoryColumns | undefined {
  const found = admittedCategory(key)
  if (found === undefined) return undefined
  return { age_group: found.age_group, under_age: found.under_age, gender: found.gender }
}

/** A PATCH's share of the three columns: only those the pick changed, none when it changed nothing
 *  or picked a key outside the list (a row from before it, held as it was). */
export function categoryChanges(key: string, original: CategoryColumns): Partial<CategoryColumns> {
  const picked = categoryColumns(key)
  if (picked === undefined) return {}
  return {
    ...(picked.age_group === original.age_group ? {} : { age_group: picked.age_group }),
    ...(picked.under_age === original.under_age ? {} : { under_age: picked.under_age }),
    ...(picked.gender === original.gender ? {} : { gender: picked.gender }),
  }
}
